import { validationProblemsTag } from '../../../apis/apiValidationProblems';
import {
  ApiOperationType,
  DeleteRegionFlowGroupRequest,
  Operation,
  RegionFlowGroupResponse,
  ValidationArea,
} from '@ydistri/api-sdk';
import { apiSlice, TemplateOrCalculation } from '../../../apis/api';
import {
  CalculationsCollection,
  RegionsCollection,
  TemplatesCollection,
} from '../../../swagger/collections';
import {
  SignalConfigurationChanged,
  SignalConfigurationsChanged,
} from '../../../signalr/signalrInterfaces';
import { signalrClient } from '../../../signalr/client';
import { getTags } from '../../../apis/apiLib';

const { TAGS, TAGS_ARRAY } = getTags('regionFlowGroups');

interface ModifyRegionToGroupPayload {
  templateId: number;
  regionId: number;
  regionGroupId: number;
}

interface RemoveRegionFromGroupPayload extends DeleteRegionFlowGroupRequest {
  templateId: number;
}

export const apiRegionFlowGroups = apiSlice
  .enhanceEndpoints({
    addTagTypes: TAGS_ARRAY,
  })
  .injectEndpoints({
    endpoints: builder => ({
      postRegionToGroup: builder.mutation<RegionFlowGroupResponse[], ModifyRegionToGroupPayload>({
        invalidatesTags: (result, error, { templateId }) => [
          validationProblemsTag({ type: 'Template', id: templateId }, ValidationArea.Regions),
        ],
        queryFn: async payload => {
          const { data } = await TemplatesCollection.postTemplateRegionFlowGroupConfiguration(
            payload.templateId,
            payload.regionGroupId,
            {
              regionIds: [payload.regionId],
            },
          );

          return data;
        },
      }),

      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response sent
      removeRegionFromGroup: builder.mutation<void, ModifyRegionToGroupPayload>({
        invalidatesTags: (result, error, { templateId }) => [
          validationProblemsTag({ type: 'Template', id: templateId }, ValidationArea.Regions),
        ],
        queryFn: async payload => {
          const { data } = await TemplatesCollection.deleteTemplateRegionFlowConfiguration(
            payload.templateId,
            payload.regionGroupId,
            {
              regionIds: [payload.regionId],
            },
          );
          return { data };
        },
      }),

      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response sent
      removeRegionGroup: builder.mutation<void, RemoveRegionFromGroupPayload>({
        invalidatesTags: (result, error, { templateId }) => [
          validationProblemsTag({ type: 'Template', id: templateId }, ValidationArea.Regions),
        ],
        queryFn: async payload => {
          await TemplatesCollection.deleteTemplateRegionFlowGroup(payload.templateId, {
            regionFlowGroupIds: [...payload.regionFlowGroupIds],
          });
          return { data: undefined };
        },
      }),

      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response or request
      createNewRegionGroup: builder.mutation<void, TemplateOrCalculation>({
        invalidatesTags: (result, error, arg) => [
          validationProblemsTag(arg, ValidationArea.Regions),
        ],
        queryFn: async arg => {
          await TemplatesCollection.postTemplateRegionFlowGroup(arg.id);
          return { data: undefined };
        },
      }),
      getRegionFlowGroups: builder.query<RegionFlowGroupResponse[], TemplateOrCalculation>({
        queryFn: async arg => {
          const {
            data: { data: regionConfiguration },
          } = await (arg.type === 'Template'
            ? TemplatesCollection.getTemplateRegionFlowGroups(arg.id)
            : CalculationsCollection.getRegionFlowGroups(arg.id));
          return { data: regionConfiguration };
        },
        providesTags: (result, error, arg) => [
          { type: TAGS.regionFlowGroups, id: `region-flow-groups-${arg.id}` },
        ],
        async onCacheEntryAdded({ type: _, id: selectedTemplateId }, api) {
          try {
            await api.cacheDataLoaded;

            const listener1 = (signal: SignalConfigurationChanged) => {
              // eslint-disable-next-line no-console -- we want this output
              console.log('CONFIGURATION CHANGED', signal);
              if (selectedTemplateId !== signal.templateId) return;
              const groupId = signal.entityId;
              if (signal.entityType === 'RegionFlowGroup') {
                if (signal.operationType === ApiOperationType.Create) {
                  api.updateCachedData(draft => {
                    // eslint-disable-next-line no-console -- we want this output
                    console.log('INSIDE SIGNAL: ', signal, groupId, []);
                    draft.push({
                      id: groupId,
                      regions: [],
                    });
                  });
                }
              } else if (signal.entityType === 'RegionFlowGroupConfiguration') {
                if (signal.operationType === ApiOperationType.Create) {
                  signal.updates.forEach(update => {
                    const regionId = update.value;
                    RegionsCollection.regionsList({
                      conditions: [{ fieldName: 'Id', operation: Operation.Eq, value: regionId }],
                    }).then(r => {
                      api.updateCachedData(draft => {
                        const group = draft.find(g => g.id === groupId);
                        if (group) group.regions.push(r.data.data[0]);
                      });
                    });
                  });
                } else if (signal.operationType === ApiOperationType.Delete) {
                  signal.updates.forEach(update => {
                    api.updateCachedData(draft => {
                      const group = draft.find(g => g.id === groupId);
                      if (group)
                        group.regions = group.regions.filter(
                          r => r.id !== parseInt(update.value ?? '0'),
                        );
                    });
                  });
                }
              }
            };

            //TODO: This is a hack to get around the fact that the signalr client does not support multiple listeners
            const listener2 = (signal2: SignalConfigurationsChanged[]) => {
              const signal = signal2[0];
              if (selectedTemplateId !== signal.templateId) return;
              const groupId = signal.entityId;
              if (signal.entityType === 'RegionFlowGroup') {
                if (signal.operationType === ApiOperationType.Delete) {
                  api.updateCachedData(draft => {
                    draft = draft.filter(g => g.id !== groupId);
                    return draft;
                  });
                }
              }
            };

            signalrClient.on('TemplateConfigurationChanged', listener1);
            signalrClient.on('TemplateConfigurationsChanged', listener2);

            api.cacheEntryRemoved.then(() => {
              signalrClient.off('TemplateConfigurationChanged', listener1);
              signalrClient.off('TemplateConfigurationsChanged', listener2);
            });
          } catch {
            // no-op in case cache entry was removed before data was loaded
          }
          await api.cacheEntryRemoved;
        },
      }),
    }),
  });

export const {
  useGetRegionFlowGroupsQuery,
  useCreateNewRegionGroupMutation,
  usePostRegionToGroupMutation,
  useRemoveRegionFromGroupMutation,
  useRemoveRegionGroupMutation,
} = apiRegionFlowGroups;
